import * as actions from '../../action';
import { type Action } from '../../action';
import { zoomToNodes } from '../../action';
import { FontProvider, type Font } from '../../provider/font';
import { createInteractive, type Interactive } from '../../provider/interactive';
import { SelectionProvider } from '../../provider/selection';
import { TimeProvider } from '../../provider/time';
import { getDPR, TYPES } from '../constants';
import { paint } from '../index.js';

import { ActionProvider } from '../../provider/action';
import { CacheProvider } from '../../provider/cache';
import { CanvasProvider } from '../../provider/canvas';
import { CursorProvider } from '../../provider/cursor';
import { DirtyProvider } from '../../provider/dirty';
import { ImageProvider, getNodeImageCache } from '../../provider/image';
import { ScaleProvider } from '../../provider/scale';
import { StateProvider } from '../../provider/state';
import { TextProvider } from '../../provider/text';
import { TextLayoutProvider } from '../../provider/text-layout';
import { requiredProviders, AssetProvider } from '../../provider';
import { extendAPI } from './api';
import { resizeCanvas } from './canvas';
import { safelyParseJSON } from '../../common-utils';
import { createSignal, SignalValue } from '../../common-utils/signal/reactive-value';
import type { NodeLike, NodeType } from './node';
import { type RenderHooks, createProxiedHoos } from './hooks';

export { NodeLike, NodeType };

export type { Hightlight, TextStyle } from './node-types/style';
export type { TextNode } from '../../render/text';

export type RefreshTriggerValue = { type: 'node'; node: NodeLike } | { type: 'state' } | null;

/** Interactive handler and selection border style configuration */
export interface InteractiveStyle {
  /** 选框边框颜色 */
  selectionColor: string;
  /** 选框边框粗细 */
  selectionLineWidth: number;
  /** Handler 描边颜色 */
  handlerStrokeColor: string;
  /** Handler 描边粗细 */
  handlerStrokeWidth: number;
  /** Handler 形状：'rounded' 为圆形/圆角矩形，'square' 为方形/直角矩形 */
  handlerShape: 'rounded' | 'square';
  /** Handler 鼠标悬停时是否显示填充 */
  handlerHoverEffect: boolean;
  /** Handler 的大小 */
  handlerSize: number;
}
export const RENDER_CONTEXT_TYPES = {
  export: 'export',
  presentation: 'presentation',
  canvas: 'canvas',
};

export interface BasicRendererAPI {
  paint: () => void;
  requestPaint: () => void;
  /**
   * Set source data for renderer
   * @param card
   * @param options
   * @returns
   */
  setRenderingData: (
    card: Exclude<RenderContext['card'], null>,
    options?: { paddingRight?: number; padding?: number }
  ) => Promise<void>;
  // 允许后续通过 Object.assign 扩展 API
  [key: string]: any;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface RendererAPI extends BasicRendererAPI {}

export type UnattachedRenderContext = {
  dpr: number;
  locale: string;
  cacheContent: any | null;
  fonts: Font[];

  /**
   * Card act as the data source for render context
   */
  card:
    | {
        type: 'seedeGraphic';
        props: {
          nodes: Record<string, unknown>;
        };
      }
    | {
        type: string;
        props: Record<string, any>;
      }
    | null;

  disposed: boolean;
  debug: boolean;
  mode?: string;
  features: {
    scaleSafe?: boolean;
    zoom?: boolean;
  };
  publicPath: string;
  paddingRight?: number;
  providers: {
    font: FontProvider;
    cursor: CursorProvider;
    time: TimeProvider;
    selection: SelectionProvider;
    action: ActionProvider;
    state: StateProvider;
    scale: ScaleProvider;
    text: TextProvider;
    textLayout: TextLayoutProvider;
    cache: CacheProvider;
    canvas: CanvasProvider;
    asset: AssetProvider;
    image: ImageProvider;
    dirty: DirtyProvider;
    [key: string]: any;
  };
  cache: {
    loadImage: Record<string, any>;
    renderImage: Record<string, any>;
    render: Record<string, any>;
    background: Record<string, any>;
    renderText: Record<string, any>;
  };
  // FIXME: more reasonable way to notify outside of the renderer
  refreshTrigger: SignalValue<RefreshTriggerValue>;
  interactive: Partial<Interactive>;
  __textFxActive?: boolean;
  shouldSaveRender: () => boolean;
  renderIndex: number;
  lastRenderIndex: -1 | number;
  hooks: RenderHooks;
  api: BasicRendererAPI;
  clientConfig?: Record<string, unknown>;
  configs: {
    interactive: InteractiveStyle;
  };
};

export type RenderContext = UnattachedRenderContext & {
  context: CanvasRenderingContext2D;
  container: HTMLElement;
  canvas: {
    canvas: HTMLCanvasElement;
    stage: HTMLCanvasElement;
    interactive: HTMLCanvasElement;
    container: HTMLElement;
  };
  api: RendererAPI;
  detach: () => void;
};

/**
 * Create a base render context used by the stage renderer.
 * Component-specific hooks and API extensions should be assigned by the caller.
 */
export function createRenderContext(
  {
    locale = '',
    debug = false,
    features = {},
    publicPath = '',
    providers: providersOverride = {},
    actions: customActions = [],
    fonts = [],
    configs = {},
  }: {
    locale?: string;
    debug?: boolean;
    features?: {
      scaleSafe?: boolean;
      zoom?: boolean;
    };
    /**
     * The custom font list
     */
    fonts?: Font[];
    publicPath?: string;
    providers?: Record<string, any>;
    actions?: Action[];
    configs?: Partial<UnattachedRenderContext['configs']>;
  } = {},
  options?: {
    readonly?: boolean;
    features?: {
      zoom?: boolean;
    };
  }
) {
  let paintReq: number | null = null;
  const renderContext: UnattachedRenderContext = {
    get dpr() {
      return getDPR();
    },
    set dpr(_val) {},
    locale,
    cacheContent: null,
    card: null,
    debug,
    disposed: false,
    features: features || {},
    publicPath: publicPath || '',
    fonts,
    refreshTrigger: createSignal<RefreshTriggerValue>(null),
    providers: {
      font: new FontProvider(fonts),
      cursor: new CursorProvider(),
      time: new TimeProvider(),
      selection: new SelectionProvider(),
      action: new ActionProvider(),
      state: new StateProvider(),
      scale: new ScaleProvider({ safeMode: !!(features && features.scaleSafe) }),
      text: new TextProvider(),
      textLayout: new TextLayoutProvider(),
      cache: new CacheProvider(),
      canvas: new CanvasProvider({ maxActiveCanvases: 500, autoOptimize: true }),
      image: new ImageProvider(),
      dirty: new DirtyProvider(),
      ...providersOverride,
    } as RenderContext['providers'],
    cache: {
      loadImage: {},
      renderImage: {},
      render: {},
      background: {},
      renderText: {},
    },
    interactive: {
      x: 0,
      y: 0,
    },
    shouldSaveRender: () => {
      const nodes = renderContext.api.getNodes();
      // Check if any text nodes are being edited
      const hasEditingText = nodes.some((node) => renderContext.providers.text.isEditing(node));
      if (hasEditingText) return false;

      // Check if any images are still loading
      const hasLoadingImages = nodes.some((node) => {
        if (node.type !== TYPES.image) return false;
        const imgCache = getNodeImageCache(renderContext as RenderContext, node.id);
        return imgCache?.loading;
      });
      if (hasLoadingImages) return false;

      // Check if any frames are still loading
      const hasLoadingFrames = nodes.some((node) => {
        if (node.type !== TYPES.frame) return false;
        const imgCache = getNodeImageCache(renderContext as RenderContext, node.id);
        return node.loading || imgCache?.loading;
      });
      if (hasLoadingFrames) return false;

      const hasAnimatedFill = nodes.some((node) => {
        if (node.type !== TYPES.shape && node.type !== TYPES.frame) return false;
        if (typeof node.fill === 'string' && node.fill.includes('animated=true')) {
          return true;
        }
        return false;
      });
      if (hasAnimatedFill) return false;

      return true;
    },
    renderIndex: 0,
    lastRenderIndex: -1,
    hooks: createProxiedHoos({
      onPageChange: () => {},
      onStateChange: () => {},
      onLayerChange: () => {},
      onFrameChange: () => {
        renderContext.providers.state.frameChange$.set(Math.random());
      },
      cancelSelectionMenu: () => {},
      onSelectionChangeCallback: () => {},
      onClientConfigChange: () => {},
      onReadClientConfigChange: () => {},
    }),
    api: {
      requestPaint: () => {
        if (paintReq) return;
        paintReq = requestAnimationFrame(() => {
          paint(renderContext);
        });
      },
      paint: () => {
        paintReq = null;
        paint(renderContext);
      },
      restoreRenderingData: () => {
        const savedData = localStorage.getItem('saved-rendering-data');

        const data = safelyParseJSON(savedData || '');
        if (data && data.card) {
          renderContext.api.setRenderingData(data.card, data.options);
        }
      },
      setRenderingDataWithSave: (card, options) => {
        localStorage.setItem(
          'saved-rendering-data',
          JSON.stringify({
            card,
            options,
          })
        );

        renderContext.api.setRenderingData(card, options);
      },
      setRenderingData: (card, options) => {
        renderContext.providers.cache.clearAll();
        renderContext.card = card;
        renderContext.providers.state.setState(card.props);
        renderContext.api.layoutFrame();
        renderContext.api.paint();
        renderContext.paddingRight = options?.paddingRight;

        const firstFrame = renderContext.api.getFrames()[0];
        if (firstFrame) {
          zoomToNodes(renderContext, [firstFrame], options?.padding);
        }
        renderContext.hooks.onSelectionChangeCallback();
        return renderContext.api.loadFontsInUsed();
      },
    },
    configs: {
      interactive: Object.assign(
        {
          selectionColor: '#8b3eff',
          selectionLineWidth: 4,
          handlerStrokeColor: '#a6a6a6',
          handlerStrokeWidth: 1,
          handlerShape: 'rounded',
          handlerHoverEffect: true,
          handlerSize: 24,
        } as InteractiveStyle,
        configs.interactive ?? {}
      ),
    },
  };

  Object.keys(requiredProviders).forEach((key) => {
    if (!renderContext.providers[key]) {
      throw new Error(`Missing required provider: ${key}`);
    }
  });

  // Allow disabling zoom externally; the component can still override later
  if (features && features.zoom === false) {
    renderContext.providers.scale.isDisabled = true;
  }

  if (!options?.readonly) {
    Object.values(actions).map((action) => {
      renderContext.providers.action.add(action);
    });

    customActions.forEach((action) => {
      renderContext.providers.action.add(action);
    });
  }

  if (!options?.features?.zoom) {
    renderContext.providers.scale.isDisabled = true;
  }

  extendAPI(renderContext);

  return renderContext;
}

export default createRenderContext;

/**
 *
 * @param renderContext The renderContext created by `createRenderContext`
 * @param canvas The canvas that renderContext will attach to
 * @returns
 */
export function attach(
  renderContext: UnattachedRenderContext,
  canvas: HTMLCanvasElement,
  options: {
    readonly?: boolean;
    features?: {
      interactive?: boolean;
    };
  }
): asserts renderContext is RenderContext {
  if (!canvas.isConnected) {
    throw new Error('Canvas must be attached to the document before attaching render context.');
  }

  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    throw new Error('Failed to get canvas 2D context');
  }

  const attachedCtx = renderContext as RenderContext;

  resizeCanvas(canvas);

  attachedCtx.context = context;
  attachedCtx.canvas = {
    canvas: canvas,
    stage: canvas,
    interactive: canvas,
    container: canvas.parentElement!,
  };
  attachedCtx.container = canvas.parentElement!;

  Object.values(renderContext.providers).forEach((provider) => provider.attach(renderContext));
  // FIXME: all provider should inherit base provider.
  Object.values(renderContext.providers).forEach((provider) => provider.activate?.());

  const observer = new ResizeObserver(() => {
    resizeCanvas(canvas);
    renderContext.api.paint();
  });

  observer.observe(canvas.parentElement!, { box: 'border-box' });

  let disposeInteractive: null | (() => void) = null;
  if (!options.readonly && options.features?.interactive !== false) {
    disposeInteractive = createInteractive(renderContext as RenderContext);
    renderContext.api.requestPaint();
  }

  renderContext.api.readClientConfig();
  renderContext.api.layoutFrame();
  renderContext.api.paint();

  let loopFrameId: number | null = null;
  const loop = () => {
    renderContext.api.paint();
    loopFrameId = requestAnimationFrame(loop);
  };

  loop();

  attachedCtx.detach = () => {
    if (loopFrameId) cancelAnimationFrame(loopFrameId);
    renderContext.api.paint();
    observer.disconnect();
    Object.values(renderContext.providers).forEach((provider) => provider.dispose());
    disposeInteractive?.();
    renderContext.disposed = true;
  };
}
